import type { YMapControls, YMapControl, YMapGroupEntity, YMap, LngLat } from '@yandex/ymaps3-types'
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'
import type { Ref } from 'vue'

import type {
    DispatcherWorkplaceDataEntity,
    DispatcherWorkplaceInitializeHelperModuleOption,
    DispatcherWorkplaceDestroyHelperModuleOption,
    ClusterFeatureEntity,
    DispatcherWorkplaceProblem,
    FeaturePopupsOpenPopupOptions,
    DispatcherWorkplaceTake,
    DispatcherWorkplaceDelivery
} from '@/modules/DispatcherWorkplaceModule/types'

import { reactive, computed } from 'vue'

import mapControls from '@/modules/YandexMap3Module/map-controls'
import mapCluster from '@/modules/YandexMap3Module/map-cluster'

import DwDoneIcon from '@/modules/DispatcherWorkplaceModule/components/DwDoneIcon/index.vue'
import DwSidebarEntity from '@/modules/DispatcherWorkplaceModule/components/DwSidebar/Entity.vue'

import dwMapHelpers from '@/modules/DispatcherWorkplaceModule/dw-map-helpers'

import { useDebounce } from 'vue-composable'

const GRID_SIZE = 100
const DEBOUNCE_TIME = 1000

export default function({ openPopup, selectedFeatureId }: {
    openPopup: (options: FeaturePopupsOpenPopupOptions) => void
    selectedFeatureId: Ref<number | null>
}) {
    let mapInstance: YMap | null = null
    let problemsClusterer: any | null = null

    let problemsControlsGroup: YMapControls | null = null
    let problemsControlsControl: YMapControl | null = null
    let problemsControlsHorizontalGroup: YMapGroupEntity<{}> | null = null

    const state = reactive({
        list: [] as DispatcherWorkplaceProblem[],
        quick_filters: {
            completed_orders: false,
            wait_list: false,
            downtime: false,
            delaying: false,
            order_or_invoice_number: null as string | null,
            finded_orders_or_invoices: [] as DispatcherWorkplaceDataEntity[]
        }
    })

    const updateList = (problems: Array<DispatcherWorkplaceProblem>) => {
        state.list = problems
    }

    const onFindedEntityClicked = (entity: DispatcherWorkplaceDataEntity) => {
        dwMapHelpers.updateMapLocation({
            map: mapInstance as YMap,
            coordinates: [Number(entity.longitude as string), Number(entity.latitude as string)],
            zoom: 17
        })

        state.quick_filters.order_or_invoice_number = null
        state.quick_filters.finded_orders_or_invoices = []
    }

    const searchOrder = () => {
        if (!state.quick_filters.order_or_invoice_number) {
            state.quick_filters.finded_orders_or_invoices = []
            return
        }
        
        state.quick_filters.finded_orders_or_invoices = state.list.filter((entity) => {
            const query = (state.quick_filters.order_or_invoice_number as string).toLowerCase()

            if (entity.type === 1) {
                const entityOrderNumber = (entity as DispatcherWorkplaceTake).orderNumber.toLowerCase()
                return entityOrderNumber.includes(query)
            }

            if (entity.type === 2) {
                const entityInvoiceNumber = (entity as DispatcherWorkplaceDelivery).invoiceNumber.toLowerCase()
                return entityInvoiceNumber.includes(query)
            }
        })
    }

    const debouncedSearchOrder = useDebounce(searchOrder, DEBOUNCE_TIME)

    const initializeMapControls = () => {
        if (!problemsControlsGroup) return

        const { YMapControl } = ymaps3

        problemsControlsControl = new YMapControl()
        problemsControlsGroup.addChild(problemsControlsControl)

        problemsControlsHorizontalGroup = mapControls.createHorizontalControlsGroup()
        problemsControlsControl.addChild(problemsControlsHorizontalGroup)

        problemsControlsHorizontalGroup.addChild(
            mapControls.createIconButtonControl({
                checked: computed(() => state.quick_filters.completed_orders),
                icon: DwDoneIcon,
                onChange: () => {
                    state.quick_filters.completed_orders = !state.quick_filters.completed_orders
                    updateCluster()
                }
            })
        )

        problemsControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.wait_list),
                label: 'ЛО',
                onChange: () => {
                    state.quick_filters.wait_list = !state.quick_filters.wait_list
                    updateCluster()
                }
            })
        )

        problemsControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.downtime),
                label: 'Простой',
                onChange: () => {
                    state.quick_filters.downtime = !state.quick_filters.downtime
                    updateCluster()
                }
            })
        )

        problemsControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.delaying),
                label: 'Опаздывающий',
                onChange: () => {
                    state.quick_filters.delaying = !state.quick_filters.delaying
                    updateCluster()
                }
            })
        )

        problemsControlsHorizontalGroup.addChild(
            mapControls.createSearchControl({
                value: computed(() => state.quick_filters.order_or_invoice_number),
                placeholder: 'Поиск по номеру заказа или накладной',
                options: computed(() => state.quick_filters.finded_orders_or_invoices),
                option: DwSidebarEntity,
                onInput: (event: Event) => {
                    const target = event.target as HTMLInputElement
                    state.quick_filters.order_or_invoice_number = target.value
                    debouncedSearchOrder()
                },
                onOptionClicked: onFindedEntityClicked
            })
        )
    }

    const destroyMapControls = () => {
        if (!problemsControlsGroup || !problemsControlsControl || !problemsControlsHorizontalGroup) return

        problemsControlsControl.removeChild(problemsControlsHorizontalGroup)
        problemsControlsHorizontalGroup = null

        problemsControlsGroup.removeChild(problemsControlsControl)
        problemsControlsControl = null
    }

    const onFeatureChangeDate = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: true,
            is_add_to_wait_list: false,
            is_assign: false,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureAddToWaitList = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: true,
            is_assign: false,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureAssign = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: false,
            is_assign: true,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureSelfDelivery = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: false,
            is_assign: false,
            is_self_delivery: true,
            feature
        })
    }

    const isTakeCompleted = (entity: DispatcherWorkplaceDataEntity) => {
        if (entity.type === 1) {
            return entity.state === 1 || entity.statusId === 3
        }

        return false
    }

    const isDeliveryCompleted = (entity: DispatcherWorkplaceDataEntity) => {
        if (entity.type === 2) {
            return entity.state === 1
        }

        return false
    }

    const isFeatureEntityCompleted = (entity: DispatcherWorkplaceDataEntity) => {
        if (entity.type === 1) {
            return isTakeCompleted(entity)
        }

        if (entity.type === 2) {
            return isDeliveryCompleted(entity)
        }

        return false
    }

    const getActualClusterFeatures = () => {
        let ordersToRender: DispatcherWorkplaceDataEntity[] = []

        const completedWaitList = state.list.filter((order) => Boolean(order.waitListStatus) && isFeatureEntityCompleted(order))
        const waitList = state.list.filter((order) => Boolean(order.waitListStatus) && !isFeatureEntityCompleted(order))

        const completedDowntime = state.list.filter((order) => Boolean(order.downtimes.length) && isFeatureEntityCompleted(order))
        const downtime = state.list.filter((order) => Boolean(order.downtimes.length) && !isFeatureEntityCompleted(order))

        const delaying = state.list.filter((order) => Boolean(order.delaying))

        // ordersToRender = state.list.filter((order) => {
        //     const isCompletedWaitList = Boolean(order.waitListStatus) && isFeatureEntityCompleted(order)
        //     const isWaitList = Boolean(order.waitListStatus) && !isFeatureEntityCompleted(order)

        //     const isCompletedDowntime = Boolean(order.downtimes.length) && isFeatureEntityCompleted(order)
        //     const isDowntime = Boolean(order.downtimes.length) && !isFeatureEntityCompleted(order)

        //     const isDelaying = Boolean(order.delaying)

        //     return !isCompletedWaitList && !isWaitList && !isCompletedDowntime && !isDowntime && !isDelaying
        // })

        if (state.quick_filters.completed_orders) {
            if (state.quick_filters.wait_list) {
                ordersToRender = ordersToRender.concat(completedWaitList)
            }

            if (state.quick_filters.downtime) {
                ordersToRender = ordersToRender.concat(completedDowntime)
            }
        } else {
            if (state.quick_filters.wait_list) {
                ordersToRender = ordersToRender.concat(waitList)
            }

            if (state.quick_filters.downtime) {
                ordersToRender = ordersToRender.concat(downtime)
            }
        }

        if (state.quick_filters.delaying) {
            ordersToRender = ordersToRender.concat(delaying)
        }

        return ordersToRender
    }

    const initializeMapClusterer = async () => {
        if (!mapInstance) return

        problemsClusterer = await mapCluster.createCluster<ClusterFeatureEntity>({
            gridSize: GRID_SIZE,
            features: getActualClusterFeatures().map(dwMapHelpers.convertOrderToClusterFeature),
            markerTemplate: dwMapHelpers.convertFeatureToMarkerElement,
            clusterTemplate: dwMapHelpers.convertFeaturesToClusterElement,
            clusterPopup: (
                coordinates: LngLat,
                features: Array<ClusterFeatureEntity>,
                close: () => void,
            ) => dwMapHelpers.createClusterPopup(
                coordinates,
                features,
                selectedFeatureId.value,
                close,
                {
                    changeDate: onFeatureChangeDate,
                    addToWaitList: onFeatureAddToWaitList,
                    assign: onFeatureAssign,
                    selfDelivery: onFeatureSelfDelivery
                }
            ),
            markerPopup: (
                feature: ClusterFeatureEntity,
                close: () => void
            ) => dwMapHelpers.createMarkerPopup(
                feature,
                close,
                {
                    changeDate: onFeatureChangeDate,
                    addToWaitList: onFeatureAddToWaitList,
                    assign: onFeatureAssign,
                    selfDelivery: onFeatureSelfDelivery
                }
            )
        })

        mapInstance.addChild(problemsClusterer)
    }

    const updateCluster = () => {
        if (!problemsClusterer) return

        const features = getActualClusterFeatures().map(dwMapHelpers.convertOrderToClusterFeature)
        problemsClusterer.update({ features })
    }

    const destroyMapClusterer = () => {
        if (!mapInstance || !problemsClusterer) return

        mapInstance.removeChild(problemsClusterer)
        problemsClusterer = null
    }

    const initializeModule = (options: DispatcherWorkplaceInitializeHelperModuleOption) => {
        mapInstance = options.mapInstance
        problemsControlsGroup = options.controlsGroup

        initializeMapControls()
        initializeMapClusterer()
    }

    const destroyModule = (options: DispatcherWorkplaceDestroyHelperModuleOption) => {
        destroyMapControls()
        destroyMapClusterer()

        if (options.resetStatement) {
            state.quick_filters.order_or_invoice_number = null
            state.quick_filters.completed_orders = false
            state.quick_filters.wait_list = false
            state.quick_filters.downtime = false
            state.quick_filters.delaying = false
        }
    }

    const openClusterPopupOnMap = (dataEntity: DispatcherWorkplaceDataEntity) => {
        if (problemsClusterer) {
            if (dataEntity.type === 1) {
                problemsClusterer.openPopup(`order-${(dataEntity as DispatcherWorkplaceTake).orderNumber}`)
            }

            if (dataEntity.type === 2) {
                problemsClusterer.openPopup(`invoice-${(dataEntity as DispatcherWorkplaceDelivery).invoiceNumber}`)
            }
        }
    }

    const onEntityClicked = (dataEntity: DispatcherWorkplaceDataEntity) => {
        dwMapHelpers.updateMapLocation({
            map: mapInstance as YMap,
            coordinates: [Number(dataEntity.longitude as string), Number(dataEntity.latitude as string)],
            zoom: 17,
        })

        openClusterPopupOnMap(dataEntity)
    }

    return {
        state,
        updateList,
        initializeModule,
        destroyModule,
        onEntityClicked,
    }
}